
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import PnlHistoryTable from "../components/assetManager/PnlHistoryTable";
import { useAssetManagerWorkspace } from "../hooks/useAssetManagerWorkspace";
import { fetchAssetManagerCommissions, listAssetManagerTransactions } from "../services/investmentService";
import {
  AM_ACTIVITY_CATEGORY_LABELS,
  ASSET_MANAGER_HISTORY_META,
  ASSET_MANAGER_HISTORY_SECTIONS,
  amActivityCategoryLabel,
  formatPnlBdt,
  approvedPnlRows,
  isApprovedPnl,
  sortPnlRowsNewestFirst,
} from "../utils/assetManagerPnl";
import { projectTitleWithCode } from "../utils/projectViews";

function activityStatusBadgeClass(status) {
  const s = String(status || "").toUpperCase();
  if (["APPROVED", "COMPLETED", "EARNED", "POSTED", "CREDIT"].includes(s)) return "APPROVED";
  if (["REJECTED", "FAILED", "CANCELLED"].includes(s)) return "REJECTED";
  if (["PENDING", "PROCESSING", "SUBMITTED"].includes(s)) return "PENDING";
  return "PENDING";
}

export default function AssetManagerHistoryPage() {
  const { section: sectionParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = ASSET_MANAGER_HISTORY_SECTIONS.includes(String(sectionParam || "").toLowerCase())
    ? String(sectionParam).toLowerCase()
    : "pnl";

  const { assignedProjects, pnlRows, pnlCancellingId, openPnlEditModal, onCancelPnl } = useAssetManagerWorkspace();

  const [pnlProjectFilter, setPnlProjectFilter] = useState(searchParams.get("project") || "");
  const [commissionData, setCommissionData] = useState(null);
  const [txnData, setTxnData] = useState(null);
  const [txnProjectFilter, setTxnProjectFilter] = useState("");
  const [txnCategoryFilter, setTxnCategoryFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const pnlOpenOnly = searchParams.get("filter") === "open";

  useEffect(() => {
    const pid = searchParams.get("project");
    if (pid && section === "pnl") setPnlProjectFilter(pid);
  }, [searchParams, section]);

  useEffect(() => {
    if (section !== "pnl") return;
    const params = new URLSearchParams(searchParams);
    if (pnlProjectFilter) params.set("project", pnlProjectFilter);
    else params.delete("project");
    setSearchParams(params, { replace: true });
  }, [pnlProjectFilter, section, searchParams, setSearchParams]);

  const pnlHistoryRows = useMemo(() => {
    let rows = pnlOpenOnly ? sortPnlRowsNewestFirst(pnlRows).filter((r) => !isApprovedPnl(r)) : approvedPnlRows(pnlRows);
    rows = sortPnlRowsNewestFirst(rows);
    if (pnlProjectFilter) rows = rows.filter((r) => String(r.project) === String(pnlProjectFilter));
    return rows;
  }, [pnlRows, pnlProjectFilter, pnlOpenOnly]);

  useEffect(() => {
    if (section === "pnl") return;
    setLoading(true);
    setError("");
    const run = async () => {
      try {
        if (section === "commissions") {
          const res = await fetchAssetManagerCommissions();
          setCommissionData(res.data);
        } else if (section === "transactions") {
          const res = await listAssetManagerTransactions({
            project: txnProjectFilter || undefined,
            category: txnCategoryFilter || undefined,
          });
          setTxnData(res.data);
        }
      } catch (e) {
        setError(e.response?.data?.detail || e.message || "Failed to load history.");
      } finally {
        setLoading(false);
      }
    };
    run().catch(() => {});
  }, [section, txnProjectFilter, txnCategoryFilter]);

  const meta = ASSET_MANAGER_HISTORY_META[section];
  const activities = txnData?.activities || [];

  return (
    <div className="page history-dash">
      <header className="history-dash__header">
        <div>
          <h1 className="history-dash__title">My History</h1>
          <p className="history-dash__meta">{meta.description}</p>
        </div>
      </header>

      <nav className="history-dash__tabs" aria-label="Asset manager history sections" role="tablist">
        {ASSET_MANAGER_HISTORY_SECTIONS.map((id) => {
          const active = id === section;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`history-dash__tab${active ? " history-dash__tab--active" : ""}`}
              onClick={() =>
                navigate(
                  id === "pnl"
                    ? "/asset-manager/history/pnl"
                    : `/asset-manager/history/${id}`
                )
              }
            >
              {ASSET_MANAGER_HISTORY_META[id].label}
            </button>
          );
        })}
      </nav>

      {error && <p className="msg msg-error">{error}</p>}
      {loading && <p className="muted">Loading…</p>}

      {section === "pnl" && (
        <section className="card" style={{ padding: 14 }}>
          {pnlOpenOnly && (
            <p className="muted" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
              Showing submitted P&amp;L that is not approved yet.{" "}
              <button
                type="button"
                className="btn-link"
                style={{ padding: 0, font: "inherit" }}
                onClick={() => navigate("/asset-manager/history/pnl")}
              >
                Show approved P&amp;L only
              </button>
            </p>
          )}
          <PnlHistoryTable
            rows={pnlHistoryRows}
            assignedProjects={assignedProjects}
            projectFilter={pnlProjectFilter}
            onProjectFilterChange={setPnlProjectFilter}
            pnlCancellingId={pnlCancellingId}
            onEdit={openPnlEditModal}
            onCancel={onCancelPnl}
            emptyMessage={
              pnlOpenOnly ? "No open P&L submissions." : "No approved P&L submissions yet."
            }
          />
        </section>
      )}

      {section === "commissions" && commissionData && (
        <>
          {!commissionData.summary?.commission_enabled && (
            <p className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
              Asset manager commission is not configured (rate is 0%). Super Admin can set a commission % in platform
              Settings; earnings apply when your P&amp;L submissions are approved.
            </p>
          )}
          <div className="history-summary-grid" style={{ marginBottom: 16 }}>
            <div className="history-summary-card">
              <div className="history-summary-card__label">Your commission rate</div>
              <div className="history-summary-card__value">
                {commissionData.summary?.commission_enabled
                  ? `${commissionData.summary?.asset_manager_commission_pct ?? 0}%`
                  : "Not set"}
              </div>
            </div>
            <div className="history-summary-card">
              <div className="history-summary-card__label">Total commission earned</div>
              <div className="history-summary-card__value">
                {formatPnlBdt(commissionData.summary?.total_commission_earned_bdt)}
              </div>
            </div>
            <div className="history-summary-card">
              <div className="history-summary-card__label">Approved P&amp;L periods</div>
              <div className="history-summary-card__value">{commissionData.summary?.approved_submissions ?? 0}</div>
            </div>
            <div className="history-summary-card">
              <div className="history-summary-card__label">Payouts recorded</div>
              <div className="history-summary-card__value">{commissionData.summary?.payout_count ?? 0}</div>
            </div>
          </div>

          <section className="card" style={{ padding: 14, marginBottom: 14 }}>
            <h3 style={{ marginTop: 0 }}>Commission earnings (approved P&amp;L)</h3>
            <p className="muted" style={{ fontSize: 13 }}>
              Commission = positive gross P&amp;L × your rate at approval. Each row shows the rate snapshotted when admin
              approved the submission.
            </p>
            <div className="table-wrap">
              <table className="ops-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Period</th>
                    <th>Gross P&amp;L</th>
                    <th>Rate</th>
                    <th>Your commission</th>
                    <th>Approved</th>
                  </tr>
                </thead>
                <tbody>
                  {(commissionData.earnings || []).map((row) => (
                    <tr key={row.id}>
                      <td>{row.project_title}</td>
                      <td>
                        {row.period_start} to {row.period_end}
                      </td>
                      <td>{formatPnlBdt(row.gross_pnl)}</td>
                      <td>{row.commission_pct != null ? `${row.commission_pct}%` : "—"}</td>
                      <td>{formatPnlBdt(row.commission_bdt)}</td>
                      <td>{row.approved_at ? new Date(row.approved_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                  {(commissionData.earnings || []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="muted">
                        No approved P&amp;L yet — commission appears here after admin approval.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card" style={{ padding: 14 }}>
            <h3 style={{ marginTop: 0 }}>Payout records</h3>
            <p className="muted" style={{ margin: 0 }}>
              {Number(commissionData.summary?.payout_count || 0) > 0
                ? "Payout history will list settled commission transfers."
                : "No commission payouts recorded yet. Operations will record payouts when processed."}
            </p>
          </section>
        </>
      )}

      {section === "transactions" && txnData && (
        <>
          <div className="history-summary-grid" style={{ marginBottom: 16 }}>
            <div className="history-summary-card">
              <div className="history-summary-card__label">Activities shown</div>
              <div className="history-summary-card__value">{txnData.summary?.activity_count ?? activities.length}</div>
            </div>
            <div className="history-summary-card">
              <div className="history-summary-card__label">Assigned projects</div>
              <div className="history-summary-card__value">
                {txnData.summary?.assigned_project_count ?? assignedProjects.length}
              </div>
            </div>
            <div className="history-summary-card">
              <div className="history-summary-card__label">Commission earned</div>
              <div className="history-summary-card__value">{formatPnlBdt(txnData.summary?.commission_earned_bdt)}</div>
            </div>
            <div className="history-summary-card">
              <div className="history-summary-card__label">P&amp;L events</div>
              <div className="history-summary-card__value">{txnData.summary?.by_category?.PNL ?? 0}</div>
            </div>
          </div>

          <section className="card" style={{ padding: 14 }}>
            <p className="muted" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
              Only your operational and financial activity is listed here — not investor redemptions or share purchases.
              Use the P&amp;L and Commission tabs for detailed submission and earnings views.
            </p>
            <div className="grid-2" style={{ marginBottom: 12, maxWidth: 520 }}>
              <select className="compact-field" value={txnProjectFilter} onChange={(e) => setTxnProjectFilter(e.target.value)}>
                <option value="">All assigned projects</option>
                {assignedProjects.map((p) => (
                  <option key={p.id} value={p.id}>{projectTitleWithCode(p)}</option>
                ))}
              </select>
              <select
                className="compact-field"
                value={txnCategoryFilter}
                onChange={(e) => setTxnCategoryFilter(e.target.value)}
              >
                <option value="">All activity types</option>
                {Object.entries(AM_ACTIVITY_CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="table-wrap">
              <table className="ops-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Activity</th>
                    <th>Project</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((row) => (
                    <tr key={row.id}>
                      <td>{row.occurred_at ? new Date(row.occurred_at).toLocaleString() : "—"}</td>
                      <td>{amActivityCategoryLabel(row.category)}</td>
                      <td>{row.title || "—"}</td>
                      <td>{row.project_title || "—"}</td>
                      <td>{row.amount_bdt != null ? formatPnlBdt(row.amount_bdt) : "—"}</td>
                      <td>
                        <span className={`badge badge-${activityStatusBadgeClass(row.status)}`}>
                          {row.status || "—"}
                        </span>
                      </td>
                      <td>{row.detail || "—"}</td>
                    </tr>
                  ))}
                  {activities.length === 0 && !loading && (
                    <tr>
                      <td colSpan={7} className="muted">
                        No activity matches your filters yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
